import { jsx } from "panel";
import { ButtonEl } from "common/components/button";
import { EmailRecipientsInputEl } from "common/components/email-recipients-input";
import { InputEl } from "common/components/input";
import { SelectEl } from "common/components/select";
import { SvgIconEl } from "common/components/svg-icon";
import { TooltipEl } from "common/components/tooltip";
import { TagEl } from "common/components/tag";
import { TagSize } from "common/components/tag/types";
import { ButtonSize, ButtonTheme, ButtonType } from "common/components/button/constants";
import { DisplayType, Placement } from "common/components/tooltip/types";
import { SlackChannelSelectorEl } from "common/widgets/slack-channel-selector";
import { ToggleEl } from "common/components/toggle";
import { ToggleSize, ToggleTheme } from "common/components/toggle/types";
import { BannerEl } from "common/components/banner";
import { BannerSize, BannerTheme, BannerType } from "common/components/banner/types";
import { InsightsLineChartEl } from "common/report/insights/charts/insights-line-chart";
import { ReportStatusHero } from "common/widgets/warning-hero/presets";
import { WarningHeroReportName } from "common/widgets/warning-hero/constants";
import {
  CONFIDENCE_INTERVAL_OPTIONS,
  CUSTOM_ALERT_CRITERIA_TYPE,
  CUSTOM_ALERT_FREQUENCY,
  CUSTOM_ALERT_TYPE,
  CUSTOM_ALERT_VALUE_TYPE
} from "./constants";
import overrides from "./override.inline.styl";
import { AlertLimitsBannerEl } from "./custom-alert-limit-banner";
export function template() {
  const {
    alertName,
    alertThreshold,
    alertType,
    alertConditionText,
    alertValueType,
    alertCriteriaType,
    alertFrequency,
    allProjectMembers,
    errors,
    isEditing,
    readOnly,
    thresholdInputFocused,
    anomalyDetectionResults,
    anomalyDetectionFilteredResults
  } = this.state;
  const displayThreshold = alertThreshold && (this.attr(`is-percentage-threshold`) ? (alertThreshold * 100).toFixed(2) : alertThreshold);
  const keysAndValues = this.getResultKeysAndValues() || [];
  const selectedKeys = this.getSelectedAlertKeysToDisplay() || [];
  const isAnomalyDetectionVizEnabled = this.helpers.canFireAnomalyDetectionQuery();
  const customAlertConditionTexts = this.helpers.getCustomAlertConditionTexts();
  const { SubscriptionType } = this.helpers;
  const chartStatus = this.getReportStatus();
  const anomalyAlertLimitReached = alertCriteriaType === CUSTOM_ALERT_CRITERIA_TYPE.anomalyDetection && !this.helpers.canSaveNewAnomalyAlerts();
  const SubscriptionSectionSlackWebhook = ({
    isReadonly,
    isDeprecatedMode = false,
    isV2 = false
  }) => {
    const slackWebhookSubscriptions = this.helpers.getSubscriptionsByType(SubscriptionType.SlackWebhook);
    if (isDeprecatedMode && !slackWebhookSubscriptions.length) {
      return null;
    }
    const { sectionClassName, sectionTitleClassName, sectionValueClassName } = isV2 ? {
      sectionClassName: `v2-conditions-section`,
      sectionTitleClassName: `v2-section-subtitle`,
      sectionValueClassName: `v2-section-values`
    } : {
      sectionClassName: `section`,
      sectionTitleClassName: `section-title`,
      sectionValueClassName: `section-values`
    };
    return /* @__PURE__ */ jsx("div", {
      sel: `.custom-alert-${sectionClassName}`
    }, /* @__PURE__ */ jsx("div", {
      sel: `.custom-alert-${sectionTitleClassName}`
    }, isV2 ? `Webhooks` : `Slack webhooks`, " (", slackWebhookSubscriptions.length, ")", /* @__PURE__ */ jsx("a", {
      sel: ".custom-alert-section-title-info",
      attrs: { href: `https://api.slack.com/messaging/webhooks`, target: `_blank` }
    }, /* @__PURE__ */ jsx(SvgIconEl, {
      attrs: { icon: `info` }
    }), /* @__PURE__ */ jsx(TooltipEl, {
      attrs: { "max-width": `150px` }
    }, "Notify a slack channel via incoming webhooks."))), /* @__PURE__ */ jsx("div", {
      sel: `.custom-alert-${sectionValueClassName}.custom-alert-slack-webhook-input`
    }, slackWebhookSubscriptions.map((subscription, idx) => /* @__PURE__ */ jsx("div", {
      sel: ".custom-alert-slack-webhook-row"
    }, /* @__PURE__ */ jsx(InputEl, {
      attrs: {
        "validation-status": subscription.error ? `invalid` : ``,
        "error-message": subscription.error || ``,
        placeholder: `https://hooks.slack.com/services/T000/B000/XXXX`,
        disabled: !!isReadonly
      },
      props: {
        value: subscription.data.slack_webhook_url
      },
      on: {
        input: ({ target }) => this.helpers.updateSubscriptionData(SubscriptionType.SlackWebhook, idx, {
          slack_webhook_url: target.value
        })
      }
    }), !isReadonly ? /* @__PURE__ */ jsx(ButtonEl, {
      attrs: {
        theme: ButtonTheme.Tertiary,
        icon: `trashcan`,
        role: `button`,
        type: ButtonType.Destructive
      },
      on: {
        click: () => this.helpers.deleteSubscription(SubscriptionType.SlackWebhook, idx)
      }
    }) : null)), !isReadonly && !isDeprecatedMode ? /* @__PURE__ */ jsx(ButtonEl, {
      attrs: {
        theme: ButtonTheme.Secondary,
        icon: `plus`,
        role: `button`
      },
      on: {
        click: () => this.helpers.createDefaultSubscription(SubscriptionType.SlackWebhook)
      }
    }, "Add URL") : null));
  };
  const SubscriptionSectionSlackChannel = ({ isReadonly }) => {
    const slackChannelSubscriptions = this.helpers.getSubscriptionsByType(
      SubscriptionType.SlackChannel
    );
    const selectedChannels = slackChannelSubscriptions.map(({ data }) => {
      return { id: data.slack_channel_id, name: data.slack_channel_name };
    });
    return /* @__PURE__ */ jsx(SlackChannelSelectorEl, {
      attrs: {
        "selected-channels": selectedChannels,
        "is-readonly": isReadonly,
        "menu-width": 481
      },
      on: {
        selectedChannelsChanged: this.helpers.handleSlackChannelSelectChange
      }
    });
  };
  const CustomAlertNameInput = () => /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx(InputEl, {
    sel: ".custom-alert-name",
    attrs: {
      maxlength: 50,
      placeholder: alertName === `` ? this.helpers.DEFAULT_ALERT_NAME : alertName,
      type: `text`,
      "validation-status": errors[`alertName`] ? `invalid` : ``,
      "error-message": errors[`alertName`]
    },
    props: { value: isEditing ? alertName : `` },
    on: {
      input: this.helpers.handleAlertNameChange
    },
    class: { "custom-alert-invalid-input": !!errors[`alertName`] }
  }));
  const CustomAlertConditionKeys = () => /* @__PURE__ */ jsx(null, null, this.attr(`is-editing-mode`) ? selectedKeys.map((key) => /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-condition-key"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-condition-text"
  }, this.helpers.getAlertConditionText(key)))) : keysAndValues.map((key, idx) => /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-condition-key"
  }, key.values.length === 1 ? /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-condition-text"
  }, key.displayValues[0]), /* @__PURE__ */ jsx(TooltipEl, {
    attrs: { placement: Placement.Top }
  }, "This segment only has one value")) : /* @__PURE__ */ jsx(SelectEl, {
    sel: ".custom-alert-condition-key-dropdown",
    attrs: {
      sections: this.helpers.getAlertKeyOptions(key, idx),
      size: ButtonSize.Large,
      "selected-option": this.helpers.getAlertConditionText(selectedKeys[idx])
    },
    class: {
      "custom-alert-condition-key-error": this.helpers.displayKeyConditionErrorByIndex(idx)
    },
    on: { change: (ev) => this.helpers.handleConditionKeyChange(ev, idx) }
  }))), errors[`alertConditionKeys`] ? /* @__PURE__ */ jsx(TooltipEl, {
    attrs: {
      "always-show": true,
      placement: Placement.Top,
      type: DisplayType.Error
    }
  }, errors[`alertConditionKeys`]) : null);
  const CustomAlertEmailSection = () => /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-email-input"
  }, /* @__PURE__ */ jsx(EmailRecipientsInputEl, {
    key: "alert-recipients",
    attrs: {
      borderless: false,
      disabled: false,
      "one-line-input": true,
      recipients: JSON.stringify(this.helpers.getRecipientAttrs())
    },
    props: { projectMembersMap: new Map(allProjectMembers.map((m) => [m.email, m])) },
    on: { changed: this.helpers.handleRecipientsChanged }
  })), errors[`recipients`] ? /* @__PURE__ */ jsx(TooltipEl, {
    attrs: {
      "always-show": true,
      placement: Placement.Top,
      type: DisplayType.Error
    }
  }, "Some recipient email addresses are invalid") : null);
  const CustomAlertConditionOperatorAndThresholdV2 = () => /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-v2-condition"
  }, /* @__PURE__ */ jsx(SelectEl, {
    sel: ".custom-alert-condition-type",
    attrs: {
      options: Object.values(customAlertConditionTexts),
      "selected-option": customAlertConditionTexts[alertConditionText],
      size: ButtonSize.Large
    },
    on: {
      change: this.helpers.handleCustomAlertConditionTextChange
    }
  }), /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-condition"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-condition-threshold-controls"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-condition-threshold-input"
  }, /* @__PURE__ */ jsx(InputEl, {
    attrs: {
      placeholder: thresholdInputFocused ? `` : `0`,
      "validation-status": errors[`alertThreshold`] ? `invalid` : ``,
      "error-message": errors[`alertThreshold`],
      ...this.attr(`is-percentage-threshold`) ? { icon: `value-relative` } : {},
      "style-override": alertType !== CUSTOM_ALERT_TYPE.absolute ? `.mp-input-container .mp-input-field-container.type-text.size-large {border-radius: 9px 0px 0px 9px;}` : null
    },
    props: {
      type: `string`,
      value: alertThreshold === null ? `` : `${displayThreshold}`
    },
    on: {
      blur: this.helpers.handleThresholdBlur,
      change: this.helpers.handleThresholdChange,
      focus: this.helpers.handleThresholdFocus
    }
  })), alertType !== CUSTOM_ALERT_TYPE.absolute ? /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-condition-value-type-selection"
  }, /* @__PURE__ */ jsx(ToggleEl, {
    attrs: {
      selected: alertValueType,
      size: ToggleSize.Large,
      theme: ToggleTheme.SegmentedController,
      options: [
        { label: ``, value: CUSTOM_ALERT_VALUE_TYPE.absolute, icon: `type-number` },
        { label: ``, value: CUSTOM_ALERT_VALUE_TYPE.percent, icon: `value-relative` }
      ],
      "style-override": overrides
    },
    on: {
      select: this.helpers.handleCustomAlertValueTypeChange
    }
  })) : null), alertType !== CUSTOM_ALERT_TYPE.absolute ? /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-threshold-text-info"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-threshold-text"
  }, this.helpers.getCompareToDateSentence()), /* @__PURE__ */ jsx("a", {
    sel: ".custom-alert-threshold-info"
  }, /* @__PURE__ */ jsx(SvgIconEl, {
    attrs: { icon: `info` }
  }), /* @__PURE__ */ jsx(TooltipEl, {
    attrs: { "max-width": `150px` }
  }, "Time period is determined by the report's date range setting."))) : null)));
  const AnomalyDetectionConditionControls = () => /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-anomaly-detection-condition"
  }, /* @__PURE__ */ jsx(AlertLimitsBannerEl, {
    params: {
      showManageButton: true
    }
  }), /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-anomaly-detection-text"
  }, "Alert when ", this.hasSelectedAlertKeyForAnySegment() ? `any top 50` : `this`, " segment surges or drops outside of the expected range with"), /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-anomaly-detection-controls"
  }, /* @__PURE__ */ jsx(SelectEl, {
    sel: ".custom-alert-anomaly-detection-confidence-interval",
    attrs: {
      options: Object.keys(CONFIDENCE_INTERVAL_OPTIONS),
      "selected-option": this.helpers.getSelectedConfidenceIntervalPercent(),
      size: ButtonSize.Large
    },
    on: {
      change: this.helpers.handleConfidenceIntervalChange
    }
  }), /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-anomaly-detection-text"
  }, "confidence"), /* @__PURE__ */ jsx("a", {
    sel: ".custom-alert-anomaly-detection-info"
  }, /* @__PURE__ */ jsx(SvgIconEl, {
    attrs: { icon: `info` }
  }), /* @__PURE__ */ jsx(TooltipEl, {
    attrs: { "max-width": `150px` }
  }, this.helpers.getAnomalyDetectionInfoTooltipText())))));
  const CustomAlertCriteriaTypeSelector = () => /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-criteria-type-selection"
  }, /* @__PURE__ */ jsx(ToggleEl, {
    attrs: {
      selected: alertCriteriaType,
      size: ToggleSize.Large,
      theme: ToggleTheme.SegmentedController,
      options: [
        {
          label: `Anomaly detected`,
          value: CUSTOM_ALERT_CRITERIA_TYPE.anomalyDetection,
          ...this.helpers.isAnomalyDetectionToggleDisabled() ? {
            disabled: true,
            tooltip: { text: this.helpers.getAnomalyDetectionDisabledTooltipText(), "max-width": `300px` }
          } : { disabled: false }
        },
        { label: `Custom threshold met`, value: CUSTOM_ALERT_CRITERIA_TYPE.threshold }
      ],
      "style-override": `.mp-toggle-option { width: 100%; }`
    },
    on: {
      select: this.helpers.handleAlertCriteriaTypeChange
    }
  })), alertCriteriaType === CUSTOM_ALERT_CRITERIA_TYPE.threshold ? /* @__PURE__ */ jsx(CustomAlertConditionOperatorAndThresholdV2, null) : /* @__PURE__ */ jsx(AnomalyDetectionConditionControls, null));
  const CustomAlertModalV2 = () => /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-content"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-section"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-section-values"
  }, /* @__PURE__ */ jsx(CustomAlertNameInput, null))), /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-section"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-v2-section-title"
  }, "Criteria"), /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-v2-conditions-section.custom-alert-criteria-section"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-v2-section-subtitle"
  }, "When"), /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-v2-section-values"
  }, /* @__PURE__ */ jsx(CustomAlertConditionKeys, null), /* @__PURE__ */ jsx(CustomAlertCriteriaTypeSelector, null))), /* @__PURE__ */ jsx("div", null, isAnomalyDetectionVizEnabled && alertCriteriaType === CUSTOM_ALERT_CRITERIA_TYPE.anomalyDetection ? /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-anomaly-detection-chart"
  }, chartStatus ? /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-anomaly-detection-chart-status-hero"
  }, /* @__PURE__ */ jsx(ReportStatusHero, {
    queryError: this.helpers.isErrorResult() ? anomalyDetectionResults.error : null,
    reportName: WarningHeroReportName.Insights,
    reportStatus: chartStatus,
    showQuerySamplingCTA: false,
    showMpTips: false
  })) : /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-anomaly-detection-chart-viz"
  }, /* @__PURE__ */ jsx(InsightsLineChartEl, {
    sel: ".line-chart",
    attrs: {
      loaded: true,
      elref: `insightsLineChart`
    },
    props: {
      paramsAndResults: {
        params: this.helpers.getBookmarkParamsWithoutLegendForAnomalyDetectionPreview(),
        results: anomalyDetectionFilteredResults
      }
    }
  })), /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-anomaly-detection-text"
  }, this.helpers.getAnomaliesTriggeredSentence()))) : null)), /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-section"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-v2-section-title"
  }, "Notify"), /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-v2-conditions-section"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-v2-section-subtitle"
  }, "At most"), /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-v2-section-values"
  }, /* @__PURE__ */ jsx(ToggleEl, {
    attrs: {
      selected: this.helpers.getSelectedFrequency(),
      size: ToggleSize.Medium,
      theme: ToggleTheme.SegmentedController,
      options: this.helpers.getAlertFrequencyToggleOptions(),
      "style-override": `.mp-toggle-option { flex: 1; }`
    },
    on: {
      select: this.helpers.handleToggleFrequency
    }
  }), alertCriteriaType === CUSTOM_ALERT_CRITERIA_TYPE.anomalyDetection && alertFrequency === CUSTOM_ALERT_FREQUENCY.Hour ? /* @__PURE__ */ jsx(BannerEl, {
    attrs: {
      "body-text": this.helpers.getAnomalyDetectionBetaLabelText(),
      type: BannerType.Embedded,
      theme: BannerTheme.Info,
      size: BannerSize.ExtraSmall
    }
  }) : null)), /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-v2-conditions-section"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-v2-section-subtitle"
  }, "via Email"), /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-v2-section-values"
  }, /* @__PURE__ */ jsx(CustomAlertEmailSection, null))), this.helpers.shouldShowSlackChannelSubscriptions() ? /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-v2-conditions-section"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-v2-section-subtitle"
  }, "via Slack"), /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-v2-section-values"
  }, /* @__PURE__ */ jsx(SubscriptionSectionSlackChannel, {
    isReadonly: false
  }))), /* @__PURE__ */ jsx(SubscriptionSectionSlackWebhook, {
    isReadonly: false,
    isDeprecatedMode: true,
    isV2: true
  })) : /* @__PURE__ */ jsx(SubscriptionSectionSlackWebhook, {
    isReadonly: false,
    isV2: true
  }))));
  const CustomAlertReadOnlyCustomThresholdCriteria = () => /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-v2-condition"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-v2-condition-text"
  }, customAlertConditionTexts[alertConditionText] + ` `), /* @__PURE__ */ jsx(TagEl, {
    sel: ".custom-alert-v2-readonly-condition-threshold",
    attrs: {
      elref: `headerTag`,
      clickable: false,
      "tag-name": alertValueType === CUSTOM_ALERT_VALUE_TYPE.absolute ? ` ${displayThreshold}` : ` ${displayThreshold} %`,
      size: TagSize.Small
    }
  }), alertType !== CUSTOM_ALERT_TYPE.absolute ? /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-v2-condition-text"
  }, this.helpers.getCompareToDateSentence().toLowerCase()) : null));
  const CustomAlertReadOnlyAnomalyDetectionCriteria = () => /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-anomaly-detection-condition"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-v2-condition-text"
  }, "Alert when ", this.hasSelectedAlertKeyForAnySegment() ? `any top 50` : `this`, " segment surges or drops outside of the expected range with", /* @__PURE__ */ jsx(TagEl, {
    sel: ".custom-alert-v2-readonly-condition-threshold",
    attrs: {
      elref: `headerTag`,
      clickable: false,
      "tag-name": this.helpers.getSelectedConfidenceIntervalPercent(),
      size: TagSize.Small
    }
  }), "confidence")));
  const CustomAlertReadOnlyModalV2 = () => /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-content"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-section"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-v2-section-title"
  }, "Criteria"), /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-v2-conditions-section.custom-alert-criteria-section"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-v2-section-subtitle"
  }, "When"), /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-v2-section-values"
  }, selectedKeys.map((key) => /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-v2-condition"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-v2-condition-key-icon"
  }, /* @__PURE__ */ jsx(SvgIconEl, {
    attrs: { icon: this.helpers.getAlertConditionKeyIconName(key) }
  })), /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-v2-condition-text"
  }, this.helpers.getAlertConditionText(key)))), /* @__PURE__ */ jsx("div", null, /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-criteria-type-selection"
  }, alertCriteriaType === CUSTOM_ALERT_CRITERIA_TYPE.anomalyDetection ? `Anomaly detected` : `Custom threshold`), alertCriteriaType === CUSTOM_ALERT_CRITERIA_TYPE.anomalyDetection ? /* @__PURE__ */ jsx(CustomAlertReadOnlyAnomalyDetectionCriteria, null) : /* @__PURE__ */ jsx(CustomAlertReadOnlyCustomThresholdCriteria, null))))), /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-section"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-v2-section-title"
  }, "Notify"), /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-v2-conditions-section"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-v2-section-subtitle"
  }, "Notify"), /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-v2-section-values"
  }, this.helpers.getAlertFrequencyForReadOnly())), /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-v2-conditions-section"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-v2-section-subtitle"
  }, "via Email"), /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-v2-section-values"
  }, /* @__PURE__ */ jsx(EmailRecipientsInputEl, {
    key: "alert-recipients-readonly",
    attrs: {
      borderless: true,
      disabled: true,
      recipients: JSON.stringify(this.helpers.getRecipientAttrs())
    },
    props: { projectMembersMap: /* @__PURE__ */ new Map() }
  }))), this.helpers.getSubscriptionsByType(SubscriptionType.SlackWebhook).length > 0 ? /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-v2-conditions-section"
  }, /* @__PURE__ */ jsx(SubscriptionSectionSlackWebhook, {
    isReadonly: true,
    isDeprecatedMode: false,
    isV2: true
  })) : null, this.helpers.getSubscriptionsByType(SubscriptionType.SlackChannel).length > 0 ? /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-v2-conditions-section"
  }, /* @__PURE__ */ jsx(SubscriptionSectionSlackChannel, {
    isReadonly: true
  })) : null)));
  return /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-wrapper"
  }, /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-v2-title"
  }, this.helpers.getModalTitle()), readOnly ? /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx(CustomAlertReadOnlyModalV2, null), /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-footer"
  }, /* @__PURE__ */ jsx(ButtonEl, {
    on: { click: this.helpers.clickedCancel },
    attrs: {
      disabled: false
    }
  }, "Close"))) : /* @__PURE__ */ jsx(CustomAlertModalV2, null), !readOnly ? /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-footer"
  }, /* @__PURE__ */ jsx(ButtonEl, {
    on: { click: this.helpers.clickedCancel },
    attrs: {
      theme: ButtonTheme.Tertiary,
      disabled: false
    }
  }, "Cancel"), /* @__PURE__ */ jsx("div", {
    sel: ".custom-alert-save-section"
  }, /* @__PURE__ */ jsx(ButtonEl, {
    sel: ".custom-alert-save-button",
    on: { click: this.helpers.clickedSave },
    attrs: {
      theme: ButtonTheme.Primary,
      disabled: anomalyAlertLimitReached
    }
  }, isEditing ? `Save Alert` : `Create Alert`), anomalyAlertLimitReached ? /* @__PURE__ */ jsx(TooltipEl, {
    attrs: {
      text: this.helpers.getAnomalyAlertLimitTooltipText()
    }
  }) : null)) : null));
}
